/**
 * Modal de configuração para node de webhook
 * Permite configurar chamadas HTTP para APIs externas com suporte a:
 * - Credenciais seguras (API Key, Bearer Token, Basic Auth)
 * - Teste de requisição em tempo real
 * - Mapeamento de campos da resposta para variáveis
 * 
 * RELEASE: 2.1.0 - 2026-09-07 - Destinos da resposta migrados para o modal de variáveis graváveis.
 * RELEASE: 2.0.0 - 2024-12-24 - Adicionado teste de webhook e mapeamento de variáveis
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { FlowNode, WebhookConfig, ResponseFieldMapping, WebhookErrorCondition, WebhookErrorHandling } from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { 
  HelpCircle, 
  Globe, 
  Lock, 
  FlaskConical, 
  Save, 
  ChevronDown, 
  ChevronRight,
  ChevronUp,
  Loader2,
  CheckCircle,
  XCircle,
  Plus,
  Trash2,
  Lightbulb,
  Clock,
  AlertTriangle,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { NodeHelpDialog } from './NodeHelpDialog';
import { WebhookErrorHelpDialog } from './WebhookErrorHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { CredentialSelector } from '@/components/tenant/credentials';
import { VariableDestinationPicker } from '../VariableDestinationPicker';
import { useAuth } from '@/hooks/useAuth';
import { useWebhookTest, getValueByPath } from '@/hooks/useWebhookTest';
import { createLogger } from '@/lib/logger';

const log = createLogger('ConfigureWebhookDialog');

// Schema de validação
const webhookSchema = z.object({
  url: z.string().trim().url('URL inválida').max(2048, 'URL muito longa'),
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
  headers: z.string().optional(),
  body: z.string().optional(),
});

interface ConfigureWebhookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: WebhookConfig) => void;
  tenantId?: string;
}

/**
 * Extrai variáveis do body (formato {{variavel}})
 */
function extractVariablesFromBody(body: string): string[] {
  const regex = /\{\{([^}]+)\}\}/g;
  const variables: string[] = [];
  let match;
  
  while ((match = regex.exec(body)) !== null) {
    const varName = match[1].trim();
    if (!variables.includes(varName)) {
      variables.push(varName);
    }
  }
  
  return variables;
}

/**
 * Substitui variáveis no texto pelos valores de teste
 */
function replaceVariables(text: string, testValues: Record<string, string>): string {
  let result = text;
  for (const [key, value] of Object.entries(testValues)) {
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
  }
  return result;
}

/**
 * Gera ID único para mapeamento
 */
function generateMappingId(): string {
  return `map_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Dialog de configuração de webhook
 */
export function ConfigureWebhookDialog({ 
  open, 
  onOpenChange, 
  node, 
  onSave,
  tenantId: propTenantId,
}: ConfigureWebhookDialogProps) {
  const { currentTenant } = useAuth();
  const tenantId = propTenantId || currentTenant?.id || '';
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  
  // Estados do formulário
  const [url, setUrl] = useState(node?.data.config?.url || '');
  const [method, setMethod] = useState<'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'>(
    node?.data.config?.method || 'POST'
  );
  const [credentialId, setCredentialId] = useState<string | undefined>(
    node?.data.config?.credential_id
  );
  const [headers, setHeaders] = useState(
    node?.data.config?.headers ? JSON.stringify(node.data.config.headers, null, 2) : ''
  );
  const [body, setBody] = useState(
    typeof node?.data.config?.body === 'object' 
      ? JSON.stringify(node.data.config.body, null, 2) 
      : (node?.data.config?.body || '')
  );
  const [responseVariable, setResponseVariable] = useState(
    node?.data.config?.responseVariable || ''
  );
  const [responseMapping, setResponseMapping] = useState<ResponseFieldMapping[]>(
    node?.data.config?.responseMapping || []
  );
  const [testValues, setTestValues] = useState<Record<string, string>>(
    node?.data.config?.testValues || {}
  );
  
  // Estados de tratamento de erro
  const [errorHandlingEnabled, setErrorHandlingEnabled] = useState<boolean>(
    node?.data.config?.error_handling?.enabled || false
  );
  const [errorConditions, setErrorConditions] = useState<WebhookErrorCondition[]>(
    node?.data.config?.error_handling?.error_conditions || [{ type: 'json_path_empty', path: '' }]
  );
  const [errorAction, setErrorAction] = useState<WebhookErrorHandling['action']>(
    node?.data.config?.error_handling?.action || 'message_and_repeat'
  );
  const [errorMessage, setErrorMessage] = useState<string>(
    node?.data.config?.error_handling?.message || 'Dados não encontrados. Por favor, digite novamente.'
  );
  const [maxRetries, setMaxRetries] = useState<number>(
    node?.data.config?.error_handling?.max_retries || 3
  );
  const [exceededAction, setExceededAction] = useState<WebhookErrorHandling['exceeded_action']>(
    node?.data.config?.error_handling?.exceeded_action || 'end'
  );
  const [inputNodeId, setInputNodeId] = useState<string>(
    node?.data.config?.error_handling?.input_node_id || ''
  );
  const [exceededMessage, setExceededMessage] = useState<string>(
    node?.data.config?.error_handling?.exceeded_message || ''
  );
  
  // Estados de UI
  const [showHelp, setShowHelp] = useState(false);
  const [showErrorHelp, setShowErrorHelp] = useState(false);
  const [testSectionOpen, setTestSectionOpen] = useState(false);
  const [saveSectionOpen, setSaveSectionOpen] = useState(false);
  const [errorSectionOpen, setErrorSectionOpen] = useState(false);
  const [showAllPaths, setShowAllPaths] = useState(false);
  
  // Hook de teste de webhook
  const { isLoading: isTesting, result: testResult, suggestedPaths, testWebhook, clearResult } = useWebhookTest();

  // Variáveis detectadas no body
  const detectedVariables = useMemo(() => {
    return extractVariablesFromBody(body);
  }, [body]);

  /**
   * Sincroniza estados quando node mudar
   */
  useEffect(() => {
    if (node) {
      setUrl(node.data.config?.url || '');
      setMethod(node.data.config?.method || 'POST');
      setCredentialId(node.data.config?.credential_id);
      setHeaders(node.data.config?.headers ? JSON.stringify(node.data.config.headers, null, 2) : '');
      setBody(
        typeof node.data.config?.body === 'object' 
          ? JSON.stringify(node.data.config.body, null, 2) 
          : (node.data.config?.body || '')
      );
      setResponseVariable(node.data.config?.responseVariable || '');
      setResponseMapping(node.data.config?.responseMapping || []);
      setTestValues(node.data.config?.testValues || {});
      
      // Carrega configurações de error_handling
      const eh = node.data.config?.error_handling;
      setErrorHandlingEnabled(eh?.enabled || false);
      setErrorConditions(eh?.error_conditions || [{ type: 'json_path_empty', path: '' }]);
      setErrorAction(eh?.action || 'message_and_repeat');
      setErrorMessage(eh?.message || 'Dados não encontrados. Por favor, digite novamente.');
      setMaxRetries(eh?.max_retries || 3);
      setExceededAction(eh?.exceeded_action || 'end');
      setInputNodeId(eh?.input_node_id || '');
      setExceededMessage(eh?.exceeded_message || '');
      
      clearResult();
    }
  }, [node, clearResult]);

  /**
   * Valida e parseia JSON
   */
  const parseJSON = (jsonString: string): any => {
    if (!jsonString.trim()) return undefined;
    return JSON.parse(jsonString);
  };

  /**
   * Executa teste do webhook
   */
  /**
   * Executa teste do webhook
   * RELEASE: 2.1.0 - 2024-12-28 - Correção: Interpola variáveis também na URL e headers
   */
  const handleTest = async () => {
    if (!tenantId) {
      toast.error('Tenant não identificado');
      return;
    }

    try {
      // Valida URL original (antes da interpolação)
      webhookSchema.shape.url.parse(url);

      // ========== CORREÇÃO: Interpolar variáveis na URL ==========
      const testUrl = replaceVariables(url, testValues);

      // ========== CORREÇÃO: Interpolar variáveis nos headers antes de parsear ==========
      let parsedHeaders;
      if (headers.trim()) {
        try {
          // Primeiro interpola variáveis, depois parseia JSON
          const interpolatedHeaders = replaceVariables(headers, testValues);
          parsedHeaders = parseJSON(interpolatedHeaders);
        } catch (e) {
          toast.error('JSON de headers inválido');
          return;
        }
      }

      // Substitui variáveis no body (já existia)
      let testBody = body;
      if (body) {
        testBody = replaceVariables(body, testValues);
      }

      log.debug('Executando teste de webhook:', { 
        originalUrl: url, 
        interpolatedUrl: testUrl,
        hasInterpolatedHeaders: !!parsedHeaders 
      });
      
      // Reset do estado de expansão ao fazer novo teste
      setShowAllPaths(false);
      
      const result = await testWebhook({
        url: testUrl,           // ✅ URL interpolada
        method,
        headers: parsedHeaders, // ✅ Headers interpolados
        body: testBody,
        credential_id: credentialId,
        tenant_id: tenantId,
      });

      if (result.success) {
        toast.success(`Requisição bem-sucedida (${result.statusCode})`);
      } else if (result.error) {
        toast.error(`Erro: ${result.error}`);
      } else {
        toast.warning(`Resposta recebida com status ${result.statusCode}`);
      }

    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        toast.error('Erro ao validar configuração');
      }
    }
  };

  /**
   * Adiciona novo mapeamento de campo
   */
  const handleAddMapping = () => {
    setResponseMapping([
      ...responseMapping,
      { id: generateMappingId(), responsePath: '', variableName: '' },
    ]);
  };

  /**
   * Atualiza mapeamento existente
   */
  const handleUpdateMapping = (id: string, field: 'responsePath' | 'variableName', value: string) => {
    setResponseMapping(responseMapping.map(m => 
      m.id === id ? { ...m, [field]: value } : m
    ));
  };

  /**
   * Remove mapeamento
   */
  const handleRemoveMapping = (id: string) => {
    setResponseMapping(responseMapping.filter(m => m.id !== id));
  };

  /**
   * Adiciona sugestão de path ao mapeamento
   */
  const handleAddSuggestedPath = (path: string) => {
    // Verifica se já existe mapeamento para este path
    if (responseMapping.some(m => m.responsePath === path)) {
      toast.info('Este campo já está mapeado');
      return;
    }
    
    // Gera nome de variável sugerido baseado no path
    const suggestedVarName = path
      .replace(/\[\d+\]/g, '') // Remove índices de array
      .replace(/\./g, '_')     // Substitui pontos por underscore
      .replace(/^_/, '')       // Remove underscore inicial
      .toLowerCase();
    
    setResponseMapping([
      ...responseMapping,
      { id: generateMappingId(), responsePath: path, variableName: suggestedVarName },
    ]);
  };

  /**
   * Salva a configuração
   */
  const handleSave = () => {
    if (!node) return;

    try {
      // Valida JSONs antes de salvar
      let parsedHeaders;
      let parsedBody;

      if (headers.trim()) {
        try {
          parsedHeaders = parseJSON(headers);
        } catch (e) {
          toast.error('JSON de headers inválido');
          return;
        }
      }

      if (body.trim()) {
        try {
          parsedBody = parseJSON(body);
        } catch (e) {
          // Body não é JSON, mantém como string
          parsedBody = body;
        }
      }

      webhookSchema.parse({
        url,
        method,
        headers: headers.trim(),
        body: body.trim(),
      });

      // Monta configuração final
      const finalConfig: WebhookConfig = {
        url,
        method,
        headers: parsedHeaders,
        body: parsedBody,
        credential_id: credentialId,
        responseVariable: responseVariable || undefined,
        responseMapping: responseMapping.filter(m => m.responsePath && m.variableName),
        testValues: Object.keys(testValues).length > 0 ? testValues : undefined,
        // Tratamento de erro
        error_handling: errorHandlingEnabled ? {
          enabled: true,
          error_conditions: errorConditions.filter(c => c.type),
          action: errorAction,
          message: errorAction === 'message_and_repeat' ? errorMessage : undefined,
          max_retries: maxRetries,
          exceeded_action: exceededAction,
          exceeded_message: exceededAction === 'end' ? exceededMessage || undefined : undefined,
          input_node_id: inputNodeId || undefined,
        } : undefined,
      };

      onSave(node.id, { ...finalConfig, nickname: nicknameRef.current?.getNickname() } as WebhookConfig);
      toast.success('Configuração salva com sucesso');
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast.error(firstError.message);
      } else {
        toast.error('Erro ao salvar configuração');
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* 
          SOLUÇÃO DEFINITIVA DO SCROLL:
          - !flex !flex-col força layout flex sobre o grid padrão do DialogContent
          - h-[85vh] define altura real para o container
          - overflow-hidden impede scroll no dialog
        */}
        <DialogContent 
          className="max-w-4xl h-[85vh] !flex !flex-col overflow-hidden"
          onWheel={(e) => e.stopPropagation()} // Impede que ReactFlow capture o scroll
        >
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle>Configurar Webhook / API Externa</DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

          {/* 
            Corpo rolável: 
            - flex-1 preenche espaço disponível
            - min-h-0 permite encolher (crucial para flexbox)
            - overflow-y-auto ativa scroll nativo do navegador (mais confiável que ScrollArea)
            - overscroll-contain evita propagação do scroll para o canvas
          */}
          <div 
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain pr-2"
            onWheel={(e) => e.stopPropagation()} // Dupla proteção contra captura do ReactFlow
          >
            <div className="space-y-4 py-4 pr-2">
              {/* Campo ID do Node (somente leitura) */}
              <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

              {/* URL e Método */}
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-3 space-y-2">
                  <Label>URL da API *</Label>
                  <Input
                    placeholder="https://api.exemplo.com/endpoint"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    maxLength={2048}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Método *</Label>
                  <Select value={method} onValueChange={(v: any) => setMethod(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GET">GET</SelectItem>
                      <SelectItem value="POST">POST</SelectItem>
                      <SelectItem value="PUT">PUT</SelectItem>
                      <SelectItem value="PATCH">PATCH</SelectItem>
                      <SelectItem value="DELETE">DELETE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator className="my-4" />
              
              {/* Seção de Autenticação */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Lock className="h-4 w-4" />
                  Autenticação
                </div>
                
                {tenantId && (
                  <CredentialSelector
                    tenantId={tenantId}
                    value={credentialId}
                    onChange={setCredentialId}
                    label="Credencial"
                  />
                )}
              </div>

              <Separator className="my-4" />

              {/* Seção de Dados da Requisição */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Globe className="h-4 w-4" />
                  Dados da Requisição
                </div>

                <div className="space-y-2">
                  <Label>Headers (JSON, opcional)</Label>
                  <Textarea
                    placeholder={'{\n  "Content-Type": "application/json"\n}'}
                    value={headers}
                    onChange={(e) => setHeaders(e.target.value)}
                    rows={3}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Headers HTTP adicionais (não inclua Authorization se usar credencial)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Body (JSON)</Label>
                  <Textarea
                    placeholder={'{\n  "cpf": "{{cpf_cliente}}"\n}'}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={5}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Use variáveis com {'{{nome_variavel}}'} - elas serão substituídas durante a execução.
                    {credentialId && <><br/>🔒 A credencial será injetada automaticamente</>}
                  </p>
                </div>
              </div>

              <Separator className="my-4" />

              {/* ===================== SEÇÃO DE TESTE ===================== */}
              <Collapsible open={testSectionOpen} onOpenChange={setTestSectionOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between p-2 h-auto">
                    <div className="flex items-center gap-2 font-medium">
                      <FlaskConical className="h-4 w-4 text-amber-500" />
                      <span>Testar Requisição</span>
                    </div>
                    {testSectionOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                
                <CollapsibleContent className="space-y-4 pt-4">
                  {/* Valores de teste para variáveis detectadas */}
                  {detectedVariables.length > 0 && (
                    <div className="space-y-3 bg-muted/50 rounded-lg p-3">
                      <Label className="text-sm font-medium">Valores de Teste para Variáveis:</Label>
                      <div className="grid gap-2">
                        {detectedVariables.map((varName) => (
                          <div key={varName} className="flex items-center gap-2">
                            <Badge variant="secondary" className="font-mono text-xs">
                              {`{{${varName}}}`}
                            </Badge>
                            <Input
                              placeholder={`Valor de teste para ${varName}`}
                              value={testValues[varName] || ''}
                              onChange={(e) => setTestValues({ ...testValues, [varName]: e.target.value })}
                              className="flex-1 h-8 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Botão de teste */}
                  <Button
                    onClick={handleTest}
                    disabled={isTesting || !url}
                    className="w-full"
                    variant="default"
                  >
                    {isTesting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Testando...
                      </>
                    ) : (
                      <>
                        <FlaskConical className="h-4 w-4 mr-2" />
                        Testar Requisição
                      </>
                    )}
                  </Button>

                  {/* Resultado do teste */}
                  {testResult && (
                    <div className="space-y-3">
                      {/* Status da resposta */}
                      <div className="flex items-center gap-3">
                        {testResult.success ? (
                          <Badge variant="default" className="bg-green-500/10 text-green-600 border-green-500/20">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {testResult.statusCode} OK
                          </Badge>
                        ) : testResult.statusCode ? (
                          <Badge variant="destructive" className="bg-red-500/10 text-red-600 border-red-500/20">
                            <XCircle className="h-3 w-3 mr-1" />
                            {testResult.statusCode}
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            <XCircle className="h-3 w-3 mr-1" />
                            Erro
                          </Badge>
                        )}
                        
                        {testResult.responseTime && (
                          <Badge variant="outline" className="text-muted-foreground">
                            <Clock className="h-3 w-3 mr-1" />
                            {testResult.responseTime}ms
                          </Badge>
                        )}
                      </div>

                      {/* Erro */}
                      {testResult.error && (
                        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                          <p className="text-sm text-destructive">{testResult.error}</p>
                        </div>
                      )}

                      {/* Body da resposta - SEMPRE exibe algo após teste bem-sucedido */}
                      <div className="space-y-2">
                        <Label className="text-sm">Resposta:</Label>
                        {testResult.body && testResult.body !== '' ? (
                          <div className="bg-muted rounded-lg p-3 max-h-60 overflow-auto">
                            <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                              {typeof testResult.body === 'object' 
                                ? JSON.stringify(testResult.body, null, 2)
                                : testResult.body
                              }
                            </pre>
                          </div>
                        ) : (
                          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                            <p className="text-sm text-amber-600 dark:text-amber-400">
                              A API retornou uma resposta vazia. Verifique se os parâmetros enviados estão corretos.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Sugestões de paths com expansão */}
                      {suggestedPaths.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Lightbulb className="h-4 w-4 text-amber-500" />
                            <Label className="text-sm">
                              Campos encontrados (clique para mapear): {suggestedPaths.length} campos
                            </Label>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {/* Exibe todos ou apenas os primeiros 15 baseado no estado */}
                            {(showAllPaths ? suggestedPaths : suggestedPaths.slice(0, 15)).map((path) => (
                              <Badge
                                key={path}
                                variant="outline"
                                className="cursor-pointer hover:bg-primary/10 font-mono text-xs"
                                onClick={() => handleAddSuggestedPath(path)}
                              >
                                {path}
                              </Badge>
                            ))}
                            {/* Botão de expandir/recolher */}
                            {suggestedPaths.length > 15 && (
                              <Badge 
                                variant="secondary" 
                                className="text-xs cursor-pointer hover:bg-secondary/80 transition-colors flex items-center gap-1"
                                onClick={() => setShowAllPaths(!showAllPaths)}
                              >
                                {showAllPaths ? (
                                  <>
                                    <ChevronUp className="h-3 w-3" />
                                    Mostrar menos
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="h-3 w-3" />
                                    +{suggestedPaths.length - 15} mais
                                  </>
                                )}
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>

              <Separator className="my-4" />

              {/* ===================== SEÇÃO DE SALVAR EM VARIÁVEIS ===================== */}
              <Collapsible open={saveSectionOpen} onOpenChange={setSaveSectionOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between p-2 h-auto">
                    <div className="flex items-center gap-2 font-medium">
                      <Save className="h-4 w-4 text-blue-500" />
                      <span>Salvar em Variáveis</span>
                    </div>
                    {saveSectionOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                
                <CollapsibleContent className="space-y-4 pt-4">
                  {/* Variável para resposta completa */}
                  <div className="space-y-2">
                    <Label>Resposta completa (variável)</Label>
                    <VariableDestinationPicker
                      tenantId={tenantId}
                      value={responseVariable}
                      valueName={responseVariable}
                      onChange={(variable) => setResponseVariable(variable?.name || '')}
                      placeholder="Selecione ou crie variável..."
                    />
                    <p className="text-xs text-muted-foreground">
                      Variável que receberá toda a resposta JSON da API
                    </p>
                  </div>

                  <Separator />

                  {/* Mapeamento de campos específicos */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Mapeamento de Campos</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddMapping}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Adicionar
                      </Button>
                    </div>

                    {responseMapping.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Nenhum mapeamento configurado.
                        <br />
                        <span className="text-xs">
                          Use o teste acima para descobrir os campos disponíveis.
                        </span>
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {responseMapping.map((mapping) => (
                          <div key={mapping.id} className="flex items-center gap-2 bg-muted/50 rounded-lg p-2">
                            {/* Campo: Caminho no JSON da resposta */}
                            <Input
                              placeholder="[0].veiculo_placa"
                              value={mapping.responsePath}
                              onChange={(e) => handleUpdateMapping(mapping.id, 'responsePath', e.target.value)}
                              className="flex-1 font-mono text-sm h-8"
                              title="Caminho no JSON da resposta (ex: [0].nome, data.items)"
                            />
                            <span className="text-muted-foreground">→</span>
                            {/* Campo: Variável de destino - usando seletor */}
                            <div className="flex-1">
                              <VariableDestinationPicker
                                tenantId={tenantId}
                                value={mapping.variableName}
                                valueName={mapping.variableName}
                                onChange={(variable) => handleUpdateMapping(mapping.id, 'variableName', variable?.name || '')}
                                placeholder="Variável destino..."
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => handleRemoveMapping(mapping.id)}
                              title="Remover mapeamento"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                            
                            {/* Preview do valor se tiver resultado de teste */}
                            {testResult?.body && mapping.responsePath && (
                              <div className="text-xs text-muted-foreground max-w-[100px] truncate" title="Valor encontrado no teste">
                                {(() => {
                                  const value = getValueByPath(testResult.body, mapping.responsePath);
                                  if (value === undefined) return '(não encontrado)';
                                  if (typeof value === 'object') return JSON.stringify(value).slice(0, 20) + '...';
                                  return String(value).slice(0, 20);
                                })()}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground">
                      Use notação de ponto para objetos aninhados: <code className="bg-muted px-1 rounded">data.usuario.nome</code>
                      <br />
                      Para arrays, use índice: <code className="bg-muted px-1 rounded">[0].campo</code> ou <code className="bg-muted px-1 rounded">items[0].id</code>
                    </p>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <Separator className="my-4" />

              {/* ===================== SEÇÃO DE TRATAMENTO DE ERRO ===================== */}
              <Collapsible open={errorSectionOpen} onOpenChange={setErrorSectionOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between p-2 h-auto">
                    <div className="flex items-center gap-2 font-medium">
                      <ShieldAlert className="h-4 w-4 text-red-500" />
                      <span>Tratamento de Erro</span>
                      {errorHandlingEnabled && (
                        <Badge variant="secondary" className="text-xs">
                          Ativo
                        </Badge>
                      )}
                    </div>
                    {errorSectionOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                
                <CollapsibleContent className="space-y-4 pt-4">
                  {/* Toggle de habilitação */}
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">Habilitar tratamento de erro</Label>
                      <p className="text-xs text-muted-foreground">
                        Permite repetir pergunta quando API retorna erro ou dados não encontrados
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={errorHandlingEnabled}
                      onChange={(e) => setErrorHandlingEnabled(e.target.checked)}
                      className="h-4 w-4 rounded border-input"
                    />
                  </div>

                  {errorHandlingEnabled && (
                    <>
                      <Separator />

                      {/* Condições de erro */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Label className="text-sm font-medium">Considerar erro quando:</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 text-muted-foreground hover:text-foreground"
                            onClick={() => setShowErrorHelp(true)}
                            title="Ajuda sobre condições de erro"
                          >
                            <HelpCircle className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          {errorConditions.map((condition, index) => (
                            <div key={index} className="flex items-center gap-2 bg-muted/50 rounded-lg p-2">
                              <Select 
                                value={condition.type} 
                                onValueChange={(v: WebhookErrorCondition['type']) => {
                                  const newConditions = [...errorConditions];
                                  newConditions[index] = { ...condition, type: v };
                                  setErrorConditions(newConditions);
                                }}
                              >
                                <SelectTrigger className="w-[200px] h-8 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="json_path_empty">Array/campo vazio</SelectItem>
                                  <SelectItem value="json_path_equals">Campo igual a valor</SelectItem>
                                  <SelectItem value="http_status">Status HTTP 4xx/5xx</SelectItem>
                                </SelectContent>
                              </Select>

                              {condition.type === 'json_path_empty' && (
                                <Input
                                  placeholder="Caminho (ex: data ou vazio para raiz)"
                                  value={condition.path || ''}
                                  onChange={(e) => {
                                    const newConditions = [...errorConditions];
                                    newConditions[index] = { ...condition, path: e.target.value };
                                    setErrorConditions(newConditions);
                                  }}
                                  className="flex-1 h-8 text-sm"
                                />
                              )}

                              {condition.type === 'json_path_equals' && (
                                <>
                                  <Input
                                    placeholder="Caminho (ex: error)"
                                    value={condition.path || ''}
                                    onChange={(e) => {
                                      const newConditions = [...errorConditions];
                                      newConditions[index] = { ...condition, path: e.target.value };
                                      setErrorConditions(newConditions);
                                    }}
                                    className="w-[120px] h-8 text-sm"
                                  />
                                  <span className="text-muted-foreground">=</span>
                                  <Input
                                    placeholder="Valor"
                                    value={condition.value || ''}
                                    onChange={(e) => {
                                      const newConditions = [...errorConditions];
                                      newConditions[index] = { ...condition, value: e.target.value };
                                      setErrorConditions(newConditions);
                                    }}
                                    className="flex-1 h-8 text-sm"
                                  />
                                </>
                              )}

                              {condition.type === 'http_status' && (
                                <span className="text-sm text-muted-foreground">
                                  Status 400, 404, 500, etc.
                                </span>
                              )}

                              {errorConditions.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive"
                                  onClick={() => setErrorConditions(errorConditions.filter((_, i) => i !== index))}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setErrorConditions([...errorConditions, { type: 'json_path_empty', path: '' }])}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Adicionar condição
                        </Button>
                      </div>

                      <Separator />

                      {/* Ação quando erro */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Quando erro detectado:</Label>
                        <Select value={errorAction} onValueChange={(v: WebhookErrorHandling['action']) => setErrorAction(v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="message_and_repeat">
                              <div className="flex items-center gap-2">
                                <RefreshCw className="h-3 w-3" />
                                Enviar mensagem e repetir pergunta anterior
                              </div>
                            </SelectItem>
                            <SelectItem value="repeat_previous">
                              <div className="flex items-center gap-2">
                                <RefreshCw className="h-3 w-3" />
                                Repetir pergunta anterior (sem mensagem)
                              </div>
                            </SelectItem>
                            <SelectItem value="continue">Continuar normalmente (ignorar erro)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Mensagem de erro */}
                      {errorAction === 'message_and_repeat' && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Mensagem de erro:</Label>
                          <Textarea
                            placeholder="Dados não encontrados. Por favor, digite novamente."
                            value={errorMessage}
                            onChange={(e) => setErrorMessage(e.target.value)}
                            rows={2}
                            className="text-sm"
                          />
                        </div>
                      )}

                      {/* Máximo de tentativas */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Máximo de tentativas:</Label>
                          <Select value={String(maxRetries)} onValueChange={(v) => setMaxRetries(parseInt(v))}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1 tentativa</SelectItem>
                              <SelectItem value="2">2 tentativas</SelectItem>
                              <SelectItem value="3">3 tentativas</SelectItem>
                              <SelectItem value="5">5 tentativas</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Após exceder tentativas:</Label>
                          <Select value={exceededAction || 'end'} onValueChange={(v: NonNullable<WebhookErrorHandling['exceeded_action']>) => setExceededAction(v)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="end">Encerrar fluxo</SelectItem>
                              <SelectItem value="continue">Continuar normalmente</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Mensagem de encerramento (quando exceededAction === 'end') */}
                      {exceededAction === 'end' && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Mensagem de encerramento:</Label>
                          <Textarea
                            placeholder="Não foi possível validar seus dados. Por favor, tente novamente mais tarde ou entre em contato com nosso suporte."
                            value={exceededMessage}
                            onChange={(e) => setExceededMessage(e.target.value)}
                            rows={2}
                            className="text-sm"
                          />
                          <p className="text-xs text-muted-foreground">
                            Mensagem enviada ao usuário antes de encerrar o fluxo
                          </p>
                        </div>
                      )}

                      {/* ID do node de input a repetir */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">ID do node de pergunta a repetir (opcional):</Label>
                        <Input
                          placeholder="Deixe vazio para usar o node anterior automaticamente"
                          value={inputNodeId}
                          onChange={(e) => setInputNodeId(e.target.value)}
                          className="font-mono text-sm"
                        />
                        <p className="text-xs text-muted-foreground">
                          Copie o ID do node de pergunta/input do painel de edição
                        </p>
                      </div>
                    </>
                  )}
                </CollapsibleContent>
              </Collapsible>

              {/* Aviso de segurança */}
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    <strong>Atenção:</strong> Certifique-se de que a URL é confiável. O webhook será chamado automaticamente durante a execução do fluxo.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t pt-4 mt-auto">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType="webhook" />
      <WebhookErrorHelpDialog open={showErrorHelp} onOpenChange={setShowErrorHelp} />
    </>
  );
}
